import { OnboardingSurveyAnswerDto } from "@repo/api/user";

export type SurveyQuestionOption = {
  value: string;
  label: string;
  imageUrl?: string;
};

export type SurveyQuestion = {
  id: string;
  title: string;
  options: SurveyQuestionOption[];
};

export function getQuestion(
  earlierAnswers: OnboardingSurveyAnswerDto[],
): SurveyQuestion | null {
  const answeredQuestionIds = earlierAnswers.map((answer) => answer.questionId);

  if (!answeredQuestionIds.includes("ROLE")) {
    return {
      id: "ROLE",
      title: "What best describes your role?",
      options: [
        {
          value: "AGENT",
          label: "Agent",
          imageUrl: "/onboarding-survey/handshake.svg",
        },
        {
          value: "PHOTOGRAPHER",
          label: "Photographer",
          imageUrl: "/onboarding-survey/camera.svg",
        },
        {
          value: "MARKETING",
          label: "Marketing",
          imageUrl: "/onboarding-survey/marketing.svg",
        },
        {
          value: "PROPERTY_OWNER",
          label: "Property owner",
          imageUrl: "/onboarding-survey/house.svg",
        },
      ],
    };
  }

  const roleAnswer = earlierAnswers.find(
    (answer) => answer.questionId === "ROLE",
  );

  const role = roleAnswer?.answer;
  const isPropertyOwner = roleAnswer?.answer === "PROPERTY_OWNER";

  if (!answeredQuestionIds.includes("LISTINGS") && role && !isPropertyOwner) {
    const defaultTitle = "How many listings do you handle per year?";

    const specificTitles: Record<string, string> = {
      PHOTOGRAPHER: "How many property shoots are you doing each year?",
      MARKETING: "How many listings do you market per year?",
    };

    const defaultOptions = [
      {
        value: "1-10",
        label: "1-10",
        imageUrl: "/onboarding-survey/house-small-1.svg",
      },
      {
        value: "11-30",
        label: "11-30",
        imageUrl: "/onboarding-survey/house-small-2.svg",
      },
      {
        value: "30-60",
        label: "30-60",
        imageUrl: "/onboarding-survey/house-small-3.svg",
      },
      {
        value: "61+",
        label: "61+",
        imageUrl: "/onboarding-survey/house-small-4.svg",
      },
    ];

    const specificOptions: Record<
      string,
      { value: string; label: string; imageUrl: string }[]
    > = {
      PHOTOGRAPHER: [
        {
          value: "1-20",
          label: "1-20",
          imageUrl: "/onboarding-survey/house-small-1.svg",
        },
        {
          value: "21-60",
          label: "21-60",
          imageUrl: "/onboarding-survey/house-small-2.svg",
        },
        {
          value: "61-120",
          label: "61-120",
          imageUrl: "/onboarding-survey/house-small-3.svg",
        },
        {
          value: "121+",
          label: "121+",
          imageUrl: "/onboarding-survey/house-small-4.svg",
        },
      ],
      MARKETING: [
        {
          value: "1-20",
          label: "1-20",
          imageUrl: "/onboarding-survey/house-small-1.svg",
        },
        {
          value: "21-60",
          label: "21-60",
          imageUrl: "/onboarding-survey/house-small-2.svg",
        },
        {
          value: "61-120",
          label: "61-120",
          imageUrl: "/onboarding-survey/house-small-3.svg",
        },
        {
          value: "121+",
          label: "121+",
          imageUrl: "/onboarding-survey/house-small-4.svg",
        },
      ],
    };

    return {
      id: "LISTINGS",
      title: specificTitles[role] ?? defaultTitle,
      options: specificOptions[role] ?? defaultOptions,
    };
  }

  if (!answeredQuestionIds.includes("SOURCE")) {
    return {
      id: "SOURCE",
      title: "Where did you first hear about Amplifiles?",
      options: [
        { value: "GOOGLE", label: "Google Search" },
        { value: "CHAT_GPT", label: "ChatGPT" },
        { value: "FACEBOOK", label: "Facebook" },
        { value: "INSTAGRAM", label: "Instagram" },
        { value: "YOUTUBE", label: "Youtube" },
        { value: "TIKTOK", label: "TikTok" },
        { value: "REDDIT", label: "Reddit" },
        { value: "LINKEDIN", label: "LinkedIn" },
        { value: "FRIEND", label: "From friend" },
        { value: "BLOG", label: "Blog" },
        { value: "EVENT", label: "Event" },
        { value: "OTHER", label: "Other" },
      ],
    };
  }

  return null;
}
